import { ScoreCardType } from "../models/scoreCard.model";

/**
 * Formats a numeric score based on the scorecard type
 * @param score - The numeric score value
 * @param type - The scorecard type
 * @param t - Optional translation function for boolean values (e.g., (key: string) => string)
 */
export function formatScore(
    score: number | undefined,
    type: ScoreCardType = ScoreCardType.COUNT,
    t?: (key: string) => string
): string {
    if (score === undefined || score === null) {
        return '-';
    }
    switch (type) {
        case ScoreCardType.COUNT:
            return Math.floor(score).toString();

        case ScoreCardType.GRADE:
            // Format with 1-2 decimal places, remove trailing zeros
            return parseFloat(score.toFixed(2)).toString();

        case ScoreCardType.TIME_HH_MM: {
            const totalSeconds = Math.floor(score);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }

        case ScoreCardType.TIME_MM_SS: {
            const totalSeconds = Math.floor(score);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }

        case ScoreCardType.TIME_HH_MM_SS: {
            const totalSeconds = Math.floor(score);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }

        case ScoreCardType.PERCENTAGE:
            return `${Math.round(score)}%`;

        case ScoreCardType.BOOLEAN:
            if (t) {
                return score === 0 || score === 0.0 ? t('no') : t('yes');
            }
            return score === 0 || score === 0.0 ? '0' : '1';

        default:
            return score.toString();
    }
}

/**
 * Parses user input back to numeric value based on scorecard type
 */
export function parseScore(input: string, type: ScoreCardType = ScoreCardType.COUNT): number {
    switch (type) {
        case ScoreCardType.TIME_HH_MM: {
            // Parse "HH:MM" format to seconds
            const parts = input.split(':');
            if (parts.length === 2 && parts[0] !== undefined && parts[1] !== undefined) {
                const hours = parseInt(parts[0], 10) || 0;
                const minutes = parseInt(parts[1], 10) || 0;
                return hours * 3600 + minutes * 60;
            }
            // Fallback: try to parse as number (seconds)
            return parseFloat(input) || 0;
        }

        case ScoreCardType.TIME_MM_SS: {
            // Parse "MM:SS" format to seconds
            const parts = input.split(':');
            if (parts.length === 2 && parts[0] !== undefined && parts[1] !== undefined) {
                const minutes = parseInt(parts[0], 10) || 0;
                const seconds = parseInt(parts[1], 10) || 0;
                return minutes * 60 + seconds;
            }
            // Fallback: try to parse as number (seconds)
            return parseFloat(input) || 0;
        }

        case ScoreCardType.TIME_HH_MM_SS: {
            // Parse "HH:MM:SS" format to seconds
            const parts = input.split(':');
            if (parts.length === 3 && parts[0] !== undefined && parts[1] !== undefined && parts[2] !== undefined) {
                const hours = parseInt(parts[0], 10) || 0;
                const minutes = parseInt(parts[1], 10) || 0;
                const seconds = parseInt(parts[2], 10) || 0;
                return hours * 3600 + minutes * 60 + seconds;
            }
            // Fallback: try to parse as number (seconds)
            return parseFloat(input) || 0;
        }

        case ScoreCardType.BOOLEAN: {
            // Parse boolean: "1", "true", "yes" -> 1, everything else -> 0
            const lower = input.toLowerCase().trim();
            if (lower === '1' || lower === 'true' || lower === 'yes') {
                return 1;
            }
            return 0;
        }

        default:
            // For COUNT, GRADE, PERCENTAGE: standard numeric parsing
            // Replace comma with dot and parse
            const cleaned = input.replace(/,/g, '.').replace(/[^0-9.-]/g, '');
            return parseFloat(cleaned) || 0;
    }
}

/**
 * Validates if a score is within allowed range for the given type
 */
export function validateScore(score: number, type: ScoreCardType = ScoreCardType.COUNT): boolean {
    switch (type) {
        case ScoreCardType.COUNT:
            // No limit, but must be integer
            return Number.isInteger(score);

        case ScoreCardType.GRADE:
            // Must be between 0 and 10
            return score >= 0 && score <= 10;

        case ScoreCardType.PERCENTAGE:
            // Must be between 0 and 100
            return score >= 0 && score <= 100;

        case ScoreCardType.BOOLEAN:
            // Must be 0 or 1
            return score === 0 || score === 1;

        case ScoreCardType.TIME_HH_MM:
        case ScoreCardType.TIME_MM_SS:
        case ScoreCardType.TIME_HH_MM_SS:
            // Time values must be non-negative
            return score >= 0;

        default:
            return true;
    }
}